import { ApiClientError } from 'grabbitbot-legacy-ui'
import { describeError } from '../../api/errorMessages'
import { useKeyedActions } from '../../hooks/useKeyedActions'
import { describeManualCloseConflict } from './manualCloseConflict'
import { exchangeDisplayName } from './tradingConstants'

/**
 * Shared plumbing for the four B4 bot-control buttons (start bot, close
 * selected positions, delete all positions of a coin, manual buy) wherever
 * they appear (`SettingsDefaultPage`, `CoinSheetPage`, `OpenPositionsPage`).
 * Every one of these calls a BUSINESS-CRITICAL route (see
 * legacy-migration/docs/analysis/2026-09-27-grabbitbot-b4-contract.md,
 * ticket cad48171):
 *
 *  - a double click (or two different buttons for the same exchange) can
 *    race the backend's per-user+exchange advisory lock -> 409
 *    `bot_action_in_progress`;
 *  - an unreachable spotbot answers 502 with the outcome UNKNOWN — the
 *    request may already have executed, so the UI must say so and must
 *    NEVER auto-retry;
 *  - a light-buy with no usable amount answers 409 `no_amount` before ever
 *    reaching spotbot;
 *  - since spotbot 0.11.0, `close_positions_manually` answers 409 with a
 *    BARE array naming every pair that was NOT sold (ticket 2762bffe;
 *    `manualCloseConflict.ts` has the full token catalog and sources) —
 *    legacy always answered 200 `[]` regardless of outcome (audit 68ebf311),
 *    so this must never be shown or treated as success.
 *
 * Every caller must show the SAME wording for the SAME failure so a user
 * only has to learn one meaning per message.
 */

export interface BotActionFeedback {
  tone: 'warning' | 'error'
  text: string
}

function rawField(err: ApiClientError, field: string): unknown {
  return err.raw && typeof err.raw === 'object' && !Array.isArray(err.raw) ? (err.raw as Record<string, unknown>)[field] : undefined
}

/** "3 open positions" / "1 open position" — the backend's `count` field;
 * a missing/invalid count falls back to the count-free wording. */
function countOf(err: ApiClientError, singular: string, plural: string): string | null {
  const count = rawField(err, 'count')
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 1) return null
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`
}

/** Connection create/delete refusals (nothing was created / deleted). */
function describeConnectionRefusal(err: ApiClientError): BotActionFeedback | null {
  if (err.status !== 409 && err.status !== 404) return null
  switch (err.code) {
    case 'exchange_already_connected': {
      const existing = rawField(err, 'exchangeId')
      const name = typeof existing === 'string' ? exchangeDisplayName(existing) : 'this exchange'
      const what = typeof existing === 'string' ? `a ${name} connection` : 'a connection to this exchange'
      return { tone: 'warning', text: `You already have ${what}. An exchange can only be connected once.` }
    }
    case 'connection_has_open_positions': {
      const n = countOf(err, 'open position', 'open positions')
      return {
        tone: 'warning',
        text: n
          ? `This connection still has ${n}. Close or delete ${n === '1 open position' ? 'it' : 'them'} first.`
          : 'This connection still has open positions. Close or delete them first.',
      }
    }
    case 'connection_has_open_orders': {
      const n = countOf(err, 'open order', 'open orders')
      return {
        tone: 'warning',
        text: n
          ? `This connection still has ${n} on the exchange. Wait for ${n === '1 open order' ? 'it' : 'them'} to finish or cancel ${n === '1 open order' ? 'it' : 'them'} first.`
          : 'This connection still has open orders on the exchange. Wait for them to finish or cancel them first.',
      }
    }
    case 'connection_not_found':
    case 'bot_running':
    case 'bot_run_in_progress':
    case 'bot_halted':
      return { tone: 'warning', text: describeError(err) }
    default:
      return null
  }
}

/** Delete-connection wording: same as every bot action, except a 502 means
 * the DELETE may have gone through — say so instead of "check positions". */
export function describeDeleteConnectionError(err: unknown): BotActionFeedback {
  if (err instanceof ApiClientError && err.status === 502) {
    return { tone: 'error', text: 'Outcome unknown — reload the page to see whether the connection was deleted before retrying.' }
  }
  return describeBotActionError(err)
}

export function describeBotActionError(err: unknown): BotActionFeedback {
  if (err instanceof ApiClientError) {
    const refusal = describeConnectionRefusal(err)
    if (refusal) return refusal
    if (err.status === 409 && err.code === 'bot_action_in_progress') {
      return { tone: 'warning', text: 'Another action is already running for this bot — please wait for it to finish.' }
    }
    if (err.status === 409 && err.code === 'no_amount') {
      return { tone: 'warning', text: 'Enter an amount before opening a position.' }
    }
    // `close_positions_manually` (spotbot passthrough, since spotbot 0.11.0):
    // the 409 body is a BARE JSON array naming every pair that was NOT sold
    // (or a whole-request reason) — never `{error: ...}`. `ApiClientError.raw`
    // is the parsed body, so an array here can only be this shape (the
    // `bot_action_in_progress` / `no_amount` object shapes are handled above).
    // See manualCloseConflict.ts for the full token catalog and sources.
    if (err.status === 409 && Array.isArray(err.raw)) {
      return describeManualCloseConflict(err.raw)
    }
    // Contract: an unreachable spotbot is 502 — the request may have already
    // executed at spotbot. Never claim success, never suggest a retry here.
    if (err.status === 502) {
      return { tone: 'error', text: 'Outcome unknown — check positions before retrying.' }
    }
    return { tone: 'error', text: describeError(err, 'The bot action failed') }
  }
  return { tone: 'error', text: describeError(err, 'The bot action failed') }
}

/** Per-action busy/feedback tracking for the bot-control buttons — the
 * shared keyed hook (`src/hooks/useKeyedActions.ts`, whose synchronous
 * `inFlight` ref IS the double-click guard) with this module's wording. */
export function useBotActions() {
  return useKeyedActions(describeBotActionError)
}
